import { useState, useEffect } from "react";
import { Shield, Plus, Trash2, AlertTriangle, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { pluginApi, automodApi, serverApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { useApiMutation } from "@/hooks/useApi";
import { toast } from "sonner";

interface AutoModRule {
  id: string;
  type: string;
  enabled: boolean;
  threshold: number | null;
  action: string;
  duration: number | null;
  whitelist: string[];
  config: any;
}

interface AutoModViolation {
  id: string;
  userId: string;
  username?: string | null;
  ruleType?: string | null;
  action: string;
  content?: string | null;
  createdAt?: string;
  violatedAt?: string;
}

interface Props {
  serverId: string;
}

interface AutoModPluginConfig {
  showPublicModerationEmbeds: boolean;
  staffLogChannelId: string;
}

interface ScamHashEntry {
  id: string;
  phash: string;
  label: string;
  source?: string;
  serverId?: string | null;
  createdAt?: string;
}

const ruleTypes = [
  { id: 'flood', name: 'Flood', icon: '💨', description: 'Troppi messaggi in pochi secondi (indipendente dal contenuto)' },
  { id: 'spam', name: 'Spam identico', icon: '🚫', description: 'Stesso messaggio ripetuto più volte' },
  { id: 'caps', name: 'Caps', icon: '🔠', description: 'Troppo maiuscolo' },
  { id: 'links', name: 'Link', icon: '🔗', description: 'Link non autorizzati' },
  { id: 'invites', name: 'Inviti', icon: '📨', description: 'Inviti Discord' },
  { id: 'profanity', name: 'Parolacce', icon: '🤬', description: 'Linguaggio offensivo' },
  { id: 'emojis', name: 'Emoji', icon: '😀', description: 'Troppi emoji' },
  { id: 'mentions', name: 'Mention', icon: '@', description: 'Troppi mention' },
  { id: 'scam-images', name: 'Immagini scam', icon: '🖼️', description: 'OCR + pHash su allegati immagine' },
];

const actions = [
  { id: 'delete', name: 'Elimina Messaggio', color: 'text-yellow-500' },
  { id: 'warn', name: 'Warning', color: 'text-orange-500' },
  { id: 'mute', name: 'Mute', color: 'text-red-500' },
  { id: 'kick', name: 'Kick', color: 'text-red-600' },
  { id: 'ban', name: 'Ban', color: 'text-red-700' },
];

export const AutoModPlugin = ({ serverId }: Props) => {
  const [rules, setRules] = useState<AutoModRule[]>([]);
  const [violations, setViolations] = useState<AutoModViolation[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [pluginConfig, setPluginConfig] = useState<AutoModPluginConfig>({
    showPublicModerationEmbeds: true,
    staffLogChannelId: '',
  });
  const [scamHashes, setScamHashes] = useState<ScamHashEntry[]>([]);
  const [newScamImageUrl, setNewScamImageUrl] = useState('');
  const [newScamLabel, setNewScamLabel] = useState('scam');
  const [addingScamHash, setAddingScamHash] = useState(false);
  const safeStats = {
    total: Number(stats?.total || 0),
    last24Hours: Number(stats?.last24Hours || 0),
    last7Days: Number(stats?.last7Days || 0),
    uniqueUsers: Number(stats?.uniqueUsers || 0),
  };

  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const automodPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'automod');
  const isEnabled = automodPlugin?.enabled ?? true;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled ? pluginApi.enablePlugin(serverId, 'automod') : pluginApi.disablePlugin(serverId, 'automod'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateAutoModConfig, isPending: savingPluginConfig } = useApiMutation(
    (config: AutoModPluginConfig) => pluginApi.updatePluginConfig(serverId, 'automod', config),
    {
      invalidateQueries: [['servers', serverId, 'plugins']],
      onSuccess: () => toast.success('Configurazione AutoMod salvata'),
      onError: () => toast.error('Errore salvataggio configurazione AutoMod'),
    }
  );

  useEffect(() => {
    fetchRules();
    fetchStats();
    fetchViolations();
    fetchScamHashes();
  }, [serverId]);

  useEffect(() => {
    if (!automodPlugin?.config) return;
    const cfg = automodPlugin.config as any;
    setPluginConfig({
      showPublicModerationEmbeds: cfg.showPublicModerationEmbeds ?? true,
      staffLogChannelId: cfg.staffLogChannelId || '',
    });
  }, [automodPlugin]);

  const fetchRules = async () => {
    try {
      const data = await automodApi.getRules(serverId);
      const list = Array.isArray(data) ? data : [];

      const dedupedByType = list.reduce((acc: AutoModRule[], rule: any) => {
        const existingIndex = acc.findIndex((r) => r.type === rule.type);
        if (existingIndex === -1) {
          acc.push(rule);
          return acc;
        }

        const existingUpdatedAt = new Date((acc[existingIndex] as any).updatedAt || 0).getTime();
        const candidateUpdatedAt = new Date(rule.updatedAt || 0).getTime();
        if (candidateUpdatedAt >= existingUpdatedAt) {
          acc[existingIndex] = rule;
        }

        return acc;
      }, []);

      setRules(dedupedByType);
    } catch (error) {
      console.error('Error fetching rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await automodApi.getStats(serverId);
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({ total: 0, last24Hours: 0, last7Days: 0, uniqueUsers: 0 });
    }
  };

  const fetchViolations = async () => {
    try {
      const data = await automodApi.getViolations(serverId, 20, 0);
      setViolations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching violations:', error);
      setViolations([]);
    }
  };

  const fetchScamHashes = async () => {
    try {
      const data = await automodApi.getScamHashes(serverId);
      setScamHashes(Array.isArray(data) ? data : []);
    } catch {
      setScamHashes([]);
    }
  };

  const addScamHashFromUrl = async () => {
    if (!newScamImageUrl.trim()) {
      toast.error('Inserisci URL immagine');
      return;
    }
    setAddingScamHash(true);
    try {
      await automodApi.addScamHash(serverId, {
        imageUrl: newScamImageUrl.trim(),
        label: newScamLabel.trim() || 'scam',
      });
      setNewScamImageUrl('');
      await fetchScamHashes();
      toast.success('Hash scam aggiunto al database');
    } catch (error) {
      console.error('Error adding scam hash:', error);
      toast.error('Errore aggiunta hash (esegui migration_add_scam_hashes.sql?)');
    } finally {
      setAddingScamHash(false);
    }
  };

  const removeScamHash = async (hashId: string) => {
    try {
      await automodApi.deleteScamHash(serverId, hashId);
      await fetchScamHashes();
      toast.success('Hash rimosso');
    } catch {
      toast.error('Errore rimozione hash');
    }
  };

  const createRule = async (type: string) => {
    try {
      const existingRule = rules.find((rule) => rule.type === type);
      if (existingRule) {
        await updateRule(existingRule.id, { enabled: true });
        toast.success('Filtro già presente: riattivato');
        return;
      }

      await automodApi.createRule(serverId, {
        type,
        enabled: true,
        threshold: type === 'flood' ? 5 : type === 'spam' ? 3 : type === 'caps' ? 70 : type === 'emojis' ? 10 : type === 'mentions' ? 5 : null,
        action: type === 'flood' || type === 'spam' || type === 'scam-images' ? 'warn' : 'delete',
        duration: null,
        whitelist: [],
        config: {
          ignoreAdmins: true,
          timeWindow: type === 'flood' ? 3 : type === 'spam' ? 15 : undefined,
          enableOcr: type === 'scam-images' ? true : undefined,
          enablePHash: type === 'scam-images' ? true : undefined,
          phashThreshold: type === 'scam-images' ? 0.9 : undefined,
        },
      });
      await fetchRules();
      toast.success('Regola AutoMod creata');
    } catch (error: any) {
      if (error?.status === 409) {
        const existingRuleId = error?.details?.existingRuleId;

        try {
          if (existingRuleId) {
            await automodApi.updateRule(serverId, existingRuleId, { enabled: true });
          }
          await fetchRules();
          toast.success('Regola già esistente: riattivata');
          return;
        } catch (reactivateError) {
          console.error('Error reactivating existing rule:', reactivateError);
        }
      }

      console.error('Error creating rule:', error);
      toast.error('Errore nella creazione della regola');
    }
  };

  const updateRule = async (ruleId: string, updates: Partial<AutoModRule>) => {
    const previousRules = rules;
    const optimisticRules = rules.map((rule) =>
      rule.id === ruleId
        ? {
            ...rule,
            ...updates,
            config: updates.config !== undefined
              ? { ...(rule.config || {}), ...(updates.config || {}) }
              : rule.config,
          }
        : rule
    );

    setRules(optimisticRules);

    try {
      const updatedRule = await automodApi.updateRule(serverId, ruleId, updates);

      setRules((current) => current.map((rule) =>
        rule.id === ruleId ? { ...rule, ...(updatedRule as any) } : rule
      ));
    } catch (error) {
      console.error('Error updating rule:', error);
      setRules(previousRules);
      toast.error('Errore aggiornando la regola');
    }
  };

  const deleteRule = async (ruleId: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa regola?')) return;
    try {
      await automodApi.deleteRule(serverId, ruleId);
      await fetchRules();
      toast.success('Regola eliminata');
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error('Errore eliminando la regola');
    }
  };

  const handleSavePluginConfig = () => {
    if (!pluginConfig.showPublicModerationEmbeds && !pluginConfig.staffLogChannelId.trim()) {
      toast.error('Inserisci Staff Log Channel ID se disattivi embed pubblici');
      return;
    }
    updateAutoModConfig(pluginConfig);
  };

  const missingRuleTypes = ruleTypes.filter((ruleType) =>
    !rules.some((rule) => rule.type === ruleType.id)
  );

  const getRuleTypeLabel = (ruleTypeId?: string | null) => {
    const matchedType = ruleTypes.find((ruleType) => ruleType.id === ruleTypeId);
    return matchedType?.name || ruleTypeId || 'Sconosciuta';
  };

  const getActionLabel = (actionId?: string | null) => {
    const matchedAction = actions.find((action) => action.id === actionId);
    return matchedAction?.name || actionId || 'Azione';
  };

  const getCapsMinLength = (rule: AutoModRule) => Number(rule.config?.minLength ?? 10);
  const getCapsMinUppercaseChars = (rule: AutoModRule) => Number(rule.config?.minUppercaseChars ?? 4);
  const getCapsIgnoreTitleCase = (rule: AutoModRule) => rule.config?.ignoreTitleCase !== false;

  if (loading) {
    return <div className="flex items-center justify-center h-64">Caricamento...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Auto-Moderazione
          </h2>
          <p className="text-muted-foreground">
            Configura filtri automatici per proteggere il tuo server
          </p>
          <p className="text-xs text-muted-foreground/80 mt-1">
            Le regole AutoMod sono per tipo (flood, spam identico, caps, link, inviti, parolacce, emoji, mention).
          </p>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled) => togglePlugin({ enabled })}
        />
      </div>

      {!isEnabled && (
        <div className="glass-card p-4 border border-amber-500/40 bg-amber-500/10 text-sm text-amber-300 flex items-center justify-between gap-3">
          <span>Plugin disattivato: attivalo per creare nuove regole e applicarle.</span>
          <Button size="sm" onClick={() => togglePlugin({ enabled: true })}>
            Attiva AutoMod
          </Button>
        </div>
      )}

      {isEnabled && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-4 h-4 text-primary" />
              <span className="text-sm text-muted-foreground">Totale Violazioni</span>
            </div>
            <p className="text-2xl font-bold">{safeStats.total}</p>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              <span className="text-sm text-muted-foreground">Ultimi 24h</span>
            </div>
            <p className="text-2xl font-bold">{safeStats.last24Hours}</p>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-blue-500" />
              <span className="text-sm text-muted-foreground">Ultimi 7 giorni</span>
            </div>
            <p className="text-2xl font-bold">{safeStats.last7Days}</p>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm text-muted-foreground">Utenti Unici</span>
            </div>
            <p className="text-2xl font-bold">{safeStats.uniqueUsers}</p>
          </div>
        </div>
      )}

      {isEnabled && (
        <div className="glass-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Violazioni recenti</h3>
              <p className="text-xs text-muted-foreground">Ultimi eventi rilevati da AutoMod</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchViolations}>
              Aggiorna
            </Button>
          </div>

          {violations.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">Nessuna violazione registrata al momento.</p>
          ) : (
            <div className="space-y-2">
              {violations.slice(0, 10).map((violation) => {
                const timestamp = violation.createdAt || violation.violatedAt;
                return (
                  <div
                    key={violation.id}
                    className="flex items-start justify-between gap-4 rounded-md border border-border/50 bg-muted/20 p-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {violation.username || violation.userId} · {getRuleTypeLabel(violation.ruleType)}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        Azione: {getActionLabel(violation.action)}
                        {violation.content ? ` · ${violation.content}` : ''}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {timestamp ? new Date(timestamp).toLocaleString('it-IT') : '-'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {isEnabled && (
        <div className="glass-card p-4 space-y-3">
          <div>
            <h3 className="font-semibold">Crea nuove regole</h3>
            <p className="text-xs text-muted-foreground mt-1">Attiva rapidamente i filtri non ancora configurati.</p>
          </div>

          {missingRuleTypes.length === 0 ? (
            <p className="text-sm text-muted-foreground">Tutti i filtri disponibili sono già configurati.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {missingRuleTypes.map((ruleType) => (
                <Button
                  key={ruleType.id}
                  variant="outline"
                  size="sm"
                  onClick={() => createRule(ruleType.id)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {ruleType.name}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="glass-card p-4 space-y-4">
        <div>
          <h3 className="font-semibold">Notifiche Moderazione</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Decidi se mostrare embed pubblici nel canale dove avviene la violazione o solo in un canale log staff.
          </p>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Mostra embed pubblici</p>
            <p className="text-xs text-muted-foreground">Se disattivato, l'embed viene inviato solo nel canale log staff.</p>
          </div>
          <Switch
            checked={pluginConfig.showPublicModerationEmbeds}
            onCheckedChange={(checked) =>
              setPluginConfig((prev) => ({ ...prev, showPublicModerationEmbeds: checked }))
            }
          />
        </div>

        {!pluginConfig.showPublicModerationEmbeds && (
          <div className="space-y-2">
            <Label htmlFor="automod-staff-log-channel">Staff Log Channel ID</Label>
            <Select
              value={pluginConfig.staffLogChannelId || 'none'}
              onValueChange={(value) =>
                setPluginConfig((prev) => ({ ...prev, staffLogChannelId: value === 'none' ? '' : value }))
              }
            >
              <SelectTrigger id="automod-staff-log-channel">
                <SelectValue placeholder="Seleziona canale log staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessun canale</SelectItem>
                {(channels as any[]).map((channel: any) => (
                  <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={handleSavePluginConfig} disabled={savingPluginConfig}>
            {savingPluginConfig ? 'Salvataggio...' : 'Salva configurazione notifiche'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ruleTypes.map((ruleType) => {
          const existingRule = rules.find(r => r.type === ruleType.id);

          return (
            <div key={ruleType.id} className="glass-card p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{ruleType.icon}</span>
                  <div>
                    <h3 className="font-semibold">{ruleType.name}</h3>
                    <p className="text-xs text-muted-foreground">{ruleType.description}</p>
                  </div>
                </div>
                {existingRule && (
                  <button
                    disabled={!isEnabled}
                    onClick={() => updateRule(existingRule.id, { enabled: !existingRule.enabled })}
                    className={cn(
                      "plugin-toggle",
                      existingRule.enabled ? "enabled" : "disabled",
                      !isEnabled && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <span
                      className={cn(
                        "inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200",
                        existingRule.enabled ? "translate-x-6" : "translate-x-1"
                      )}
                    />
                  </button>
                )}
              </div>

              {existingRule ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Azione:</span>
                    <select
                      disabled={!isEnabled}
                      value={existingRule.action}
                      onChange={(e) => updateRule(existingRule.id, { action: e.target.value })}
                      className="px-2 py-1 bg-muted rounded text-xs"
                    >
                      {actions.map(action => (
                        <option key={action.id} value={action.id}>{action.name}</option>
                      ))}
                    </select>
                  </div>

                  {existingRule.threshold !== null && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Soglia:</span>
                      <input
                        disabled={!isEnabled}
                        type="number"
                        value={existingRule.threshold}
                        onChange={(e) => {
                          const nextThreshold = Number(e.target.value);
                          updateRule(existingRule.id, {
                            threshold: Number.isFinite(nextThreshold) ? nextThreshold : existingRule.threshold,
                          });
                        }}
                        className="w-16 px-2 py-1 bg-muted rounded text-xs text-center"
                      />
                    </div>
                  )}

                  {(existingRule.type === 'flood' || existingRule.type === 'spam') && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Finestra (sec):</span>
                      <input
                        disabled={!isEnabled}
                        type="number"
                        min={1}
                        value={Number(existingRule.config?.timeWindow ?? (existingRule.type === 'flood' ? 3 : 15))}
                        onChange={(e) => {
                          const rawValue = Number(e.target.value);
                          const timeWindow = Number.isFinite(rawValue) ? Math.max(1, rawValue) : 3;
                          updateRule(existingRule.id, {
                            config: {
                              ...(existingRule.config || {}),
                              timeWindow,
                            },
                          });
                        }}
                        className="w-16 px-2 py-1 bg-muted rounded text-xs text-center"
                      />
                    </div>
                  )}

                  {existingRule.type === 'caps' && (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Min caratteri:</span>
                        <input
                          disabled={!isEnabled}
                          type="number"
                          min={1}
                          value={getCapsMinLength(existingRule)}
                          onChange={(e) => {
                            const rawValue = Number(e.target.value);
                            const minLength = Number.isFinite(rawValue) ? Math.max(1, rawValue) : 10;
                            updateRule(existingRule.id, {
                              config: {
                                ...(existingRule.config || {}),
                                minLength,
                              },
                            });
                          }}
                          className="w-16 px-2 py-1 bg-muted rounded text-xs text-center"
                        />
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Min maiuscole:</span>
                        <input
                          disabled={!isEnabled}
                          type="number"
                          min={1}
                          value={getCapsMinUppercaseChars(existingRule)}
                          onChange={(e) => {
                            const rawValue = Number(e.target.value);
                            const minUppercaseChars = Number.isFinite(rawValue) ? Math.max(1, rawValue) : 4;
                            updateRule(existingRule.id, {
                              config: {
                                ...(existingRule.config || {}),
                                minUppercaseChars,
                              },
                            });
                          }}
                          className="w-16 px-2 py-1 bg-muted rounded text-xs text-center"
                        />
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Ignora Titolo:</span>
                        <button
                          disabled={!isEnabled}
                          onClick={() =>
                            updateRule(existingRule.id, {
                              config: {
                                ...(existingRule.config || {}),
                                ignoreTitleCase: !getCapsIgnoreTitleCase(existingRule),
                              },
                            })
                          }
                          className={cn(
                            "plugin-toggle",
                            getCapsIgnoreTitleCase(existingRule) ? "enabled" : "disabled",
                            !isEnabled && "opacity-50 cursor-not-allowed"
                          )}
                          title="Se attivo, parole in Title Case come Buongiorno non vengono segnalate come CAPS"
                        >
                          <span
                            className={cn(
                              "inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200",
                              getCapsIgnoreTitleCase(existingRule) ? "translate-x-6" : "translate-x-1"
                            )}
                          />
                        </button>
                      </div>
                    </>
                  )}

                  {existingRule.type === 'scam-images' && (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">OCR attivo:</span>
                        <button
                          disabled={!isEnabled}
                          onClick={() =>
                            updateRule(existingRule.id, {
                              config: {
                                ...(existingRule.config || {}),
                                enableOcr: existingRule.config?.enableOcr === false,
                              },
                            })
                          }
                          className={cn(
                            'plugin-toggle',
                            existingRule.config?.enableOcr !== false ? 'enabled' : 'disabled',
                            !isEnabled && 'opacity-50 cursor-not-allowed'
                          )}
                        >
                          <span
                            className={cn(
                              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200',
                              existingRule.config?.enableOcr !== false ? 'translate-x-6' : 'translate-x-1'
                            )}
                          />
                        </button>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">pHash attivo:</span>
                        <button
                          disabled={!isEnabled}
                          onClick={() =>
                            updateRule(existingRule.id, {
                              config: {
                                ...(existingRule.config || {}),
                                enablePHash: existingRule.config?.enablePHash === false,
                              },
                            })
                          }
                          className={cn(
                            'plugin-toggle',
                            existingRule.config?.enablePHash !== false ? 'enabled' : 'disabled',
                            !isEnabled && 'opacity-50 cursor-not-allowed'
                          )}
                        >
                          <span
                            className={cn(
                              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200',
                              existingRule.config?.enablePHash !== false ? 'translate-x-6' : 'translate-x-1'
                            )}
                          />
                        </button>
                      </div>
                    </>
                  )}

                  {existingRule.action === 'mute' && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Durata (min):</span>
                      <input
                        disabled={!isEnabled}
                        type="number"
                        value={existingRule.duration || 10}
                        onChange={(e) => updateRule(existingRule.id, { duration: parseInt(e.target.value) })}
                        className="w-16 px-2 py-1 bg-muted rounded text-xs text-center"
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Ignora Admin:</span>
                    <button
                      disabled={!isEnabled}
                      onClick={() =>
                        updateRule(existingRule.id, {
                          config: {
                            ...(existingRule.config || {}),
                            ignoreAdmins: (existingRule.config?.ignoreAdmins ?? true) ? false : true,
                          },
                        })
                      }
                      className={cn(
                        "plugin-toggle",
                        (existingRule.config?.ignoreAdmins ?? true) ? "enabled" : "disabled",
                        !isEnabled && "opacity-50 cursor-not-allowed"
                      )}
                      title="Se attivo, AutoMod non punisce gli admin"
                    >
                      <span
                        className={cn(
                          "inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200",
                          (existingRule.config?.ignoreAdmins ?? true) ? "translate-x-6" : "translate-x-1"
                        )}
                      />
                    </button>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!isEnabled}
                    onClick={() => deleteRule(existingRule.id)}
                    className="w-full text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Elimina Regola
                  </Button>
                </div>
              ) : (
                <Button
                  disabled={!isEnabled}
                  onClick={() => createRule(ruleType.id)}
                  className="w-full"
                  variant="outline"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {isEnabled ? 'Attiva Filtro' : 'Attiva plugin per creare'}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {rules.some((r) => r.type === 'scam-images' && r.enabled) && (
        <div className="glass-card p-4 space-y-4">
          <div>
            <h3 className="font-semibold">Database immagini scam (pHash)</h3>
            <p className="text-sm text-muted-foreground">
              Aggiungi screenshot scam noti: il bot calcola il pHash e blocca immagini simili (&gt;90%).
            </p>
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <Input
              placeholder="URL immagine Discord/CDN"
              value={newScamImageUrl}
              onChange={(e) => setNewScamImageUrl(e.target.value)}
              disabled={!isEnabled || addingScamHash}
            />
            <Input
              placeholder="Etichetta"
              value={newScamLabel}
              onChange={(e) => setNewScamLabel(e.target.value)}
              className="md:w-40"
              disabled={!isEnabled || addingScamHash}
            />
            <Button onClick={addScamHashFromUrl} disabled={!isEnabled || addingScamHash}>
              {addingScamHash ? 'Calcolo...' : 'Aggiungi'}
            </Button>
          </div>
          {scamHashes.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessun hash nel database. L&apos;OCR euristica resta attivo.</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {scamHashes.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between text-xs bg-muted/50 rounded p-2 gap-2">
                  <div className="min-w-0">
                    <span className="font-medium">{entry.label}</span>
                    <span className="text-muted-foreground ml-2 font-mono truncate">{entry.phash}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeScamHash(entry.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
